import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { LayoutDashboard, Settings, CreditCard, LogOut } from "lucide-react";

/**
 * AccountMenu — compact avatar dropdown shown in the marketing navbar when a
 * user is logged in. Mirrors the app Topbar profile menu, in playful styling.
 */
const ITEMS = [
  { icon: LayoutDashboard, label: "Dashboard", to: "/dashboard" },
  { icon: Settings, label: "Settings", to: "/settings" },
  { icon: CreditCard, label: "Billing", to: "/billing" },
];

export function AccountMenu({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const initial =
    user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || "?";

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Account menu"
        data-testid="nav-account"
        className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-ink bg-candy font-display text-sm font-extrabold text-white shadow-sticker-xs transition-transform duration-200 ease-bouncy hover:-translate-y-0.5"
      >
        {initial}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full z-50 mt-3 w-56 rounded-2xl border-2 border-ink bg-white p-2 shadow-sticker-md"
          style={{ animation: "pop-in 180ms cubic-bezier(0.34,1.56,0.64,1) both" }}
        >
          <div className="mb-1 truncate border-b border-ink/10 px-3 py-2 font-mono text-2xs uppercase tracking-[0.14em] text-ink/60">
            {user?.email}
          </div>
          {ITEMS.map(({ icon: Icon, label, to }) => (
            <Link
              key={label}
              to={to}
              onClick={() => setOpen(false)}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm font-semibold text-ink/70 transition-colors hover:bg-ink/5 hover:text-ink"
            >
              <Icon size={15} strokeWidth={2} /> {label}
            </Link>
          ))}
          <button
            onClick={() => {
              setOpen(false);
              onLogout?.();
            }}
            className="mt-0.5 flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm font-semibold text-ink/70 transition-colors hover:bg-ink/5 hover:text-ink"
            data-testid="logout-btn"
          >
            <LogOut size={15} strokeWidth={2} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export default AccountMenu;
